import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock } from "lucide-react";
import api from "../lib/api";
import { FadeUp, SectionTag } from "../components/site/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-navy pt-16 min-h-screen blueprint-grid grain">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-14">
        <FadeUp className="lg:col-span-5">
          <SectionTag dark>Contact // Direct Line</SectionTag>
          <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl mt-6">
            Talk to an operator.
          </h1>
          <p className="text-slate-400 mt-6 leading-relaxed max-w-md">
            Questions about the Blueprint, fit, or enrollment? Send a message —
            a real person reads every one.
          </p>
          <div className="mt-10 border border-white/10 bg-charcoal/60 p-6">
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-safety" />
              <p className="font-display font-bold text-white">Response time</p>
            </div>
            <p className="text-slate-400 text-sm mt-2">Under 4 hours on business days. Weekends: next business day.</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-4">[SLA: &lt; 4H BUSINESS]</p>
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="lg:col-span-7">
          {sent ? (
            <div className="bg-charcoal border border-white/10 p-12 text-center" data-testid="contact-success">
              <CheckCircle2 size={48} className="text-safety mx-auto" />
              <h2 className="font-display font-extrabold text-white text-3xl mt-6">Message sent.</h2>
              <p className="text-slate-400 mt-3">We'll reply within 4 business hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-charcoal border border-white/10 p-6 sm:p-10 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Name</label>
                  <input required minLength={2} data-testid="contact-name-input" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none" />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Email</label>
                  <input required type="email" data-testid="contact-email-input" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-2">Message</label>
                <textarea required minLength={10} rows={6} data-testid="contact-message-input" value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your business and what you're trying to fix…"
                  className="w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-safety outline-none resize-none" />
              </div>
              <button type="submit" data-testid="contact-submit-btn" disabled={sending}
                className="bg-safety hover:bg-safety-hover disabled:opacity-50 text-white font-semibold px-8 py-4 transition-colors">
                {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </FadeUp>
      </div>
    </div>
  );
}
